import { OrderDirection } from "_shared/_api/ordering/order-direction";
import Guid from "_shared/guids/guid";
import AHDate from "_shared/dates/date";
import DocumentFilterSchema, {
  DocumentFilterJSON,
  DocumentFilterParametersProps,
} from "documents/_api/commands/_schema/document-filter-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { DocumentQueryParams } from "documents/_api/commands/_schema/document-query-schema";

export default class DocumentQuery {
  public readonly filterParameters: DocumentFilterParameters;
  public readonly orderParameters: DocumentOrderParameters;
  public readonly paginationParameters: PaginationParameters;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.filterParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...this.paginationParameters.searchParams,
    ]);
  }

  public get cacheKey(): string {
    const params = this.searchParams;
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }

  public constructor(params: DocumentQueryParams) {
    this.filterParameters = new DocumentFilterParameters(params.filterParameters);
    this.orderParameters = new DocumentOrderParameters(params.orderParameters);
    this.paginationParameters = PaginationParameters.create(params.paginationParameters ?? {});
  }

  public isEqualTo(other: DocumentQuery | null | undefined): boolean {
    if (!other) return false;
    return (
      this.orderParameters.isEqualTo(other?.orderParameters) &&
      this.filterParameters.isEqualTo(other?.filterParameters) &&
      this.paginationParameters.isEqualTo(other?.paginationParameters)
    );
  }

  public toString(): string {
    return (
      `DocumentQuery {\n` +
      `Order Parameters: { ${this.orderParameters.toString()} },\n` +
      `Filter Parameters: { ${this.filterParameters.toString()} },\n` +
      `Pagination Parameters: { ${this.paginationParameters.toString()} }\n` +
      `}`
    );
  }
}

export class DocumentFilterParameters {
  public readonly name?: string;
  public readonly minCreatedDate?: AHDate;
  public readonly maxCreatedDate?: AHDate;
  public readonly query?: string;
  public readonly entityClass?: string;
  public readonly entityId?: Guid;
  public readonly contexts?: string[];
  public readonly ownershipScope?: "own" | "entity" | "all";
  public readonly creatorId?: Guid;
  public readonly creatorEntityId?: Guid;
  public readonly isArchived?: boolean;

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.name) searchParams.append("name", this.name);
    if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toJSON());
    if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toJSON());
    if (this.query) searchParams.append("query", this.query);
    if (this.entityClass) searchParams.append("entityClass", this.entityClass);
    if (this.entityId) searchParams.append("entityId", this.entityId.value);
    if (this.contexts) {
      this.contexts.forEach((context) => searchParams.append("contexts", context));
    }
    if (this.ownershipScope) searchParams.append("ownershipScope", this.ownershipScope);
    if (this.creatorId) searchParams.append("creatorId", this.creatorId.value);
    if (this.creatorEntityId) searchParams.append("creatorEntityId", this.creatorEntityId.value);
    if (this.isArchived !== undefined) searchParams.append("isArchived", String(this.isArchived));
    return searchParams;
  }

  public constructor(filters: DocumentFilterParametersProps) {
    this.name = filters.name;
    this.minCreatedDate = filters.minCreatedDate;
    this.maxCreatedDate = filters.maxCreatedDate;
    this.query = filters.query;
    this.entityClass = filters.entityClass;
    this.entityId = filters.entityId;
    this.contexts = filters.contexts;
    this.ownershipScope = filters.ownershipScope;
    this.creatorId = filters.creatorId;
    this.creatorEntityId = filters.creatorEntityId;
    this.isArchived = filters.isArchived;
  }

  public toJSON(): DocumentFilterJSON {
    return DocumentFilterSchema.serialize(this);
  }

  public isEqualTo(other: DocumentFilterParameters | null | undefined): boolean {
    if (!other) return false;
    return Boolean(
      this.name === other.name &&
      this.minCreatedDate?.isEqualTo(other.minCreatedDate) &&
      this.maxCreatedDate?.isEqualTo(other.maxCreatedDate) &&
      this.query === other.query &&
      this.entityClass === other.entityClass &&
      ((this.entityId === undefined && other.entityId === undefined) ||
        (this.entityId !== undefined && other.entityId !== undefined && this.entityId.isEqualTo(other.entityId))) &&
      ((this.contexts === undefined && other.contexts === undefined) ||
        (this.contexts !== undefined &&
          other.contexts !== undefined &&
          this.contexts.length === other.contexts.length &&
          this.contexts.every((context) => other.contexts!.includes(context)))) &&
      this.ownershipScope === other.ownershipScope &&
      ((this.creatorId === undefined && other.creatorId === undefined) ||
        (this.creatorId !== undefined && other.creatorId !== undefined && this.creatorId.isEqualTo(other.creatorId))) &&
      ((this.creatorEntityId === undefined && other.creatorEntityId === undefined) ||
        (this.creatorEntityId !== undefined &&
          other.creatorEntityId !== undefined &&
          this.creatorEntityId.isEqualTo(other.creatorEntityId))) &&
      this.isArchived === other.isArchived
    );
  }

  public toString(): string {
    return `DocumentFilterParameters {
      name: ${this.name},
      minCreatedDate: ${this.minCreatedDate},
      maxCreatedDate: ${this.maxCreatedDate},
      query: ${this.query},
      entityClass: ${this.entityClass},
      entityId: ${this.entityId},
      contexts: ${this.contexts},
      ownershipScope: ${this.ownershipScope},
      creatorId: ${this.creatorId},
      creatorEntityId: ${this.creatorEntityId},
      isArchived: ${this.isArchived}
    }`;
  }
}

export enum DocumentOrderField {
  Name = "name",
  Label = "context",
  Created = "created",
  Size = "fileSize",
}

export type DocumentOrderParametersProps = {
  orderBy?: DocumentOrderField;
  orderDir?: OrderDirection;
};

export class DocumentOrderParameters {
  public readonly orderBy?: DocumentOrderField;
  public readonly orderDir?: OrderDirection;

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.orderBy) searchParams.append("orderBy", this.orderBy);
    if (this.orderDir) searchParams.append("orderDir", this.orderDir);
    return searchParams;
  }

  public constructor(params: DocumentOrderParametersProps) {
    this.orderBy = params.orderBy;
    this.orderDir = params.orderDir;
  }

  public isEqualTo(other: DocumentOrderParameters | null | undefined): boolean {
    return this.orderBy === other?.orderBy && this.orderDir === other?.orderDir;
  }

  public toString(): string {
    return `DocumentOrder {
      orderBy: ${this.orderBy},
      orderDir: ${this.orderDir}
    }`;
  }
}
